import { MONTHS, WEEKDAYS, addDays, weekdayOf, type DateKey } from './time';
import { startOfWeek } from './habits';

export type WeekStart = 'sun' | 'mon';

export const weekStartIndex = (w: WeekStart): 0 | 1 => (w === 'mon' ? 1 : 0);

/** The 7 dates of the week containing `anchor`. */
export function weekDays(anchor: DateKey, weekStart: WeekStart): DateKey[] {
  const start = startOfWeek(anchor, weekStartIndex(weekStart));
  return [0, 1, 2, 3, 4, 5, 6].map((i) => addDays(start, i));
}

/** Short weekday headers in display order: ["Sun", "Mon", …] or ["Mon", …, "Sun"]. */
export function weekdayHeaders(weekStart: WeekStart): string[] {
  const s = weekStartIndex(weekStart);
  return [0, 1, 2, 3, 4, 5, 6].map((i) => WEEKDAYS[(i + s) % 7]!.slice(0, 3));
}

export interface MonthCell {
  date: DateKey;
  day: number;
  inMonth: boolean;
}

/** A 6×7 grid covering the month of `anchor` (fixed height, so the layout never jumps). */
export function monthGrid(anchor: DateKey, weekStart: WeekStart): MonthCell[][] {
  const [y, m] = anchor.split('-');
  const first = `${y}-${m}-01`;
  const start = startOfWeek(first, weekStartIndex(weekStart));
  const rows: MonthCell[][] = [];
  for (let w = 0; w < 6; w++) {
    const row: MonthCell[] = [];
    for (let d = 0; d < 7; d++) {
      const date = addDays(start, w * 7 + d);
      row.push({ date, day: Number(date.slice(8)), inMonth: date.slice(0, 7) === `${y}-${m}` });
    }
    rows.push(row);
  }
  return rows;
}

/** Same day-of-month `n` months away, clamped to the month's length (Jan 31 + 1 → Feb 28). */
export function addMonths(date: DateKey, n: number): DateKey {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const total = y * 12 + (m - 1) + n;
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  const last = new Date(Date.UTC(ny, nm, 0)).getUTCDate();
  const pad = (v: number) => (v < 10 ? '0' : '') + v;
  return `${ny}-${pad(nm)}-${pad(Math.min(d, last))}`;
}

/** "October 2026" */
export function monthLabel(date: DateKey): string {
  return `${MONTHS[Number(date.slice(5, 7)) - 1]} ${date.slice(0, 4)}`;
}

/** "Oct 18 – 24" or "Oct 26 – Nov 1" */
export function weekLabel(days: DateKey[]): string {
  const a = days[0]!;
  const b = days[days.length - 1]!;
  const ma = MONTHS[Number(a.slice(5, 7)) - 1]!.slice(0, 3);
  const mb = MONTHS[Number(b.slice(5, 7)) - 1]!.slice(0, 3);
  const da = Number(a.slice(8));
  const db = Number(b.slice(8));
  return ma === mb ? `${ma} ${da} – ${db}` : `${ma} ${da} – ${mb} ${db}`;
}

export const isWeekend = (date: DateKey) => {
  const d = weekdayOf(date);
  return d === 0 || d === 6;
};
